export type MarketingEmailFolder = 'all' | 'drafts' | 'scheduled' | 'sent' | 'templates' | 'analytics' | 'settings' | 'trash';

export type MarketingEmailStatus = 'draft' | 'scheduled' | 'sent' | 'failed' | 'sending';

export type AudienceSegment =
  | 'ALL_USERS'
  | 'VENDORS'
  | 'INACTIVE_USERS'
  | 'USERS_WITH_MARKETING_OPT_IN'
  | 'CUSTOM_LIST';

export type RecipientMode = 'audience' | 'custom';

export interface EmailAttachment {
  id: string;
  name: string;
  size: number;
  mimeType?: string;
  contentBase64?: string;
}

export interface MarketingEmailStats {
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  softBounced?: number;
  hardBounced?: number;
  deferred?: number;
  blocked?: number;
  invalid?: number;
  spamComplaints?: number;
  processed?: number;
  requests?: number;
}

export interface MarketingEmailCampaign {
  id: string;
  folder: Exclude<MarketingEmailFolder, 'all' | 'templates'>;
  status: MarketingEmailStatus;
  starred: boolean;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo?: string;
  /** Parent campaign this message replies to (same Gmail-style thread). */
  parentCampaignId?: string;
  /** RFC In-Reply-To header value. */
  inReplyTo?: string;
  /** RFC References header value. */
  references?: string;
  /** Brevo message ids from the last send (used for threading). */
  messageIds?: string[];
  subject: string;
  htmlBody: string;
  audienceSegment: AudienceSegment;
  recipientMode: RecipientMode;
  sendIndividually: boolean;
  includeSignature: boolean;
  signatureId?: string;
  attachments: EmailAttachment[];
  scheduledAt?: string;
  sentAt?: string;
  createdAt: string;
  updatedAt: string;
  stats?: MarketingEmailStats;
  sendError?: string;
  sentRecipients?: string[];
  recipientCount?: number;
}

export interface MarketingEmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmailSignature {
  id: string;
  name: string;
  htmlBody: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ComposeFormState {
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo: string;
  /** When set, this compose is a reply in the same email thread. */
  parentCampaignId?: string;
  inReplyTo?: string;
  references?: string;
  replyMode?: 'reply' | 'replyAll';
  subject: string;
  htmlBody: string;
  audienceSegment: AudienceSegment;
  recipientMode: RecipientMode;
  sendIndividually: boolean;
  includeSignature: boolean;
  signatureId?: string;
  attachments: EmailAttachment[];
  scheduledAt?: string;
}

export const AUDIENCE_SEGMENT_LABELS: Record<AudienceSegment, string> = {
  ALL_USERS: 'All registered users (marketing opt-in)',
  VENDORS: 'All vendors',
  INACTIVE_USERS: 'Inactive users (30+ days)',
  USERS_WITH_MARKETING_OPT_IN: 'Users with marketing emails enabled',
  CUSTOM_LIST: 'Custom email list',
};

export interface SignatureImage {
  id: string;
  name: string;
  url: string;
  mimeType?: string;
  uploadedAt?: string;
}

export interface MarketingEmailSettings {
  id: string;
  defaultFromEmail: string;
  fromEmails: string[];
  signatureImages: SignatureImage[];
  updatedAt: string;
}

export interface MarketingEmailContact {
  id: string;
  email: string;
  sources: string[];
  campaignCount: number;
  firstUsedAt: string;
  lastUsedAt: string;
}

export interface MarketingEmailAnalytics {
  totalCampaigns: number;
  sentCampaigns: number;
  draftCampaigns: number;
  scheduledCampaigns: number;
  failedCampaigns: number;
  totalRecipientsSent: number;
  totalContacts: number;
  aggregateStats: MarketingEmailStats;
  databaseStats?: MarketingEmailStats;
  brevoStats?: MarketingEmailStats | null;
  brevoRaw?: Record<string, unknown> | null;
  brevoRange?: string | null;
  statsSource?: 'brevo' | 'database';
  brevoDays?: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  unsubscribeRate?: number;
  totalWebhookEvents?: number;
  lastWebhookEventAt?: string | null;
  recentCampaigns: Array<{
    id: string;
    subject: string;
    sentAt: string | null;
    recipientCount: number;
    stats: MarketingEmailStats | null;
    from: string;
  }>;
  topContacts: Array<{ email: string; campaignCount: number; lastUsedAt: string }>;
  campaignsByMonth: Array<{ month: string; campaigns: number; recipients: number }>;
}

export const DEFAULT_SENDER_EMAIL = 'tech@vedika.health';

export const DEFAULT_FROM_EMAILS = ['tech@vedika.health', 'marketing@vedikahealth.com'];

export function appendSignatureToBody(htmlBody: string, signatureHtml?: string): string {
  if (!signatureHtml?.trim()) return htmlBody;
  return `${htmlBody}<div class="email-signature" style="margin-top:24px;">${signatureHtml}</div>`;
}
